import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import Image from 'next/image';
import { buildCssVar } from 'utils/style-override';
import { getOfferUnitPrice, getPercentSaved, convertPrice } from 'utils/price';
import { BuyFromType, BuyFromAvailability } from 'constant';
import formatCurrency from 'utils/format-currency';
import { buildImgProps } from 'utils/media';
import { onSelectItem } from 'utils/gtm';
import * as rudder from 'utils/rudderstack';
import useLocalCurrency from 'utils/useLocalCurrency';

import type { ComponentsFunnelCartOffer, ComponentsFunnelCartOfferStyle } from 'queries/graphql';

import styles from './styles.module.scss';
import { useFunnelData, SalesFunnelData } from 'utils/funnel-data-context';

export default function FunnelCartProduct({
  onClick,
  funnelOffer,
  isSelected,
  showProductUnitPrice,
  offerStyle,
}: {
  onClick: (offer: ComponentsFunnelCartOffer) => void;
  funnelOffer: ComponentsFunnelCartOffer;
  isSelected: boolean;
  showProductUnitPrice: boolean;
  offerStyle: ComponentsFunnelCartOfferStyle;
}) {
  const { offersConfig } = useFunnelData<SalesFunnelData>();

  const { name, image, calloutText, showPrice, showSavingsPercent, buyFroms, salesPageOffer } =
    funnelOffer;
  const offer = offersConfig.find(_offer => _offer.id === salesPageOffer.offer.hashedId);
  const { symbol: currencySymbol, rate: localFxRate } = useLocalCurrency();

  const hasCalloutText = !_.isEmpty(calloutText);

  const percentageOff = getPercentSaved(offer.compareAtPrice, offer.price);
  const hasSavingsPercent = showSavingsPercent && percentageOff > 0;

  const {
    headingColor,
    textColor,
    selectedBackgroundColor,
    backgroundColor,
    borderColor,
    calloutTextColor,
    calloutBackgroundColor,
  } = offerStyle || {};

  const handleClick = useCallback(() => {
    onClick(funnelOffer);

    // Quantity is reset to 1 on product select, so no quantity is considered here.
    onSelectItem(offer);
    rudder.onSelectItem(offer);
  }, [onClick, funnelOffer, offer]);

  const buttonClassNames = [styles.main, isSelected ? styles.selected : styles.unselected];

  const productPrice = useMemo(() => {
    const amazonBuyFrom = buyFroms?.find(item => {
      return item.type === BuyFromType.AMAZON;
    });

    const parsedAmazonPrice = convertPrice(offer.amazonPrice, localFxRate);
    const parsedVelocityPrice = convertPrice(offer.price, localFxRate);

    const isAmazonAvailable =
      !!parsedAmazonPrice && amazonBuyFrom?.availability === BuyFromAvailability.AVAILABLE;

    const arePricesEqual = parsedAmazonPrice === parsedVelocityPrice;

    if (isAmazonAvailable && !arePricesEqual) {
      if (showProductUnitPrice) {
        const velocityUnitPrice = getOfferUnitPrice(
          convertPrice(offer.price, localFxRate),
          offer.storeProductQuantity
        );
        const amazonUnitPrice = getOfferUnitPrice(
          convertPrice(offer.amazonPrice, localFxRate),
          offer.storeProductQuantity
        );

        return `${formatCurrency(
          Math.min(velocityUnitPrice, amazonUnitPrice),
          false,
          currencySymbol
        )}+ each`;
      }

      return `${formatCurrency(
        Math.min(parsedVelocityPrice, parsedAmazonPrice),
        false,
        currencySymbol
      )}+`;
    }

    if (showProductUnitPrice) {
      return `${formatCurrency(
        getOfferUnitPrice(offer.price, offer.storeProductQuantity),
        false,
        currencySymbol
      )} each`;
    }

    return formatCurrency(parsedVelocityPrice);
  }, [
    buyFroms,
    showProductUnitPrice,
    offer.price,
    offer.storeProductQuantity,
    offer.amazonPrice,
    currencySymbol,
    localFxRate,
  ]);

  // Truncate the product name if it is too long
  const productName = truncateProductName(name || offer.name);

  return (
    <div className={`root ${styles.container_wrapper}`}>
      <style jsx>{`
        .root {
          ${buildCssVar('--heading-color', headingColor, 'var(--heading-color-dark)')}
          ${buildCssVar('--text-color', textColor, 'var(--text-color-dark)')}
          ${buildCssVar(
            '--selected-background-color',
            selectedBackgroundColor,
            'var(--default-background-color)'
          )}
          ${buildCssVar(
            '--unselected-background-color',
            backgroundColor,
            'var(--background-color-grey)'
          )}
          ${buildCssVar('--selected-border-color', borderColor, 'var(--border-color1)')}
          ${buildCssVar('--callout-text-color', calloutTextColor, 'var(--text-color-dark)')}
          ${buildCssVar(
            '--callout-background-color',
            calloutBackgroundColor,
            'var(--default-background-color)'
          )}
        }
      `}</style>
      {hasCalloutText && <p className={styles.callout}>{calloutText}</p>}
      <button className={buttonClassNames.join(' ')} onClick={handleClick}>
        {image && (
          <Image
            {...buildImgProps(image)}
            alt={`${name || offer.name} Logo`}
            width={100}
            height={100}
            priority
          />
        )}
        <h5 className={styles.product_name}>{productName}</h5>
        {showPrice && <p className={styles.product_price}>{productPrice}</p>}
      </button>
      {hasSavingsPercent && (
        <div className={styles.saving_percent}>
          {showSavingsPercent && <p> Save {percentageOff.toFixed(0)}%</p>}
        </div>
      )}
    </div>
  );
}

function truncateProductName(productName) {
  if (productName?.length > 50) {
    return `${productName.slice(0, 50)}...`;
  }

  return productName;
}
